import {
  Box,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { HiOutlineInbox } from "react-icons/hi2";

/**
 * Mobile list design system.
 *
 * On phones the DataGrid is replaced by a stack of cards. These primitives keep
 * every list in the app on the same visual language as the users/POS dialogs:
 * brand gradient for summaries, white cards with a soft border, muted stat
 * tiles for the fields and tinted square buttons for the row actions.
 */

export const BRAND = "#0C86B6";
export const BRAND_DARK = "#0a6b92";
export const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND} 0%, #13a2d4 100%)`;

export const TONE = {
  brand: BRAND,
  success: "#039855",
  danger: "#d92d20",
  warning: "#dc6803",
  neutral: "#475467",
};

const CARD_SX = {
  bgcolor: "#ffffff",
  border: "1px solid #edf0f3",
  borderRadius: 3,
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
};

// Rounded inputs for the filter row, matching the dialog fields.
export const FILTERS_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" },
};

const toneOf = (value, tone) => {
  if (tone && tone !== "auto") return tone;
  if (tone === "auto") {
    const n = Number(value);
    if (!Number.isNaN(n) && n !== 0) return n < 0 ? TONE.danger : TONE.success;
  }
  return BRAND;
};

/** Gradient summary strip that replaces the grid's total footer. */
export const MobileSummaryCard = ({
  label = "المجموع الكلي",
  value,
  unit = "IQD",
  icon,
}) => (
  <Box
    sx={{
      background: BRAND_GRADIENT,
      borderRadius: 3,
      px: 2,
      py: 1.75,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      boxShadow: "0 6px 16px rgba(12,134,182,0.25)",
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.35 }}>
        {value}
        {unit ? (
          <Box
            component="span"
            sx={{ fontSize: 12, fontWeight: 500, opacity: 0.85, mr: 0.5 }}
          >
            {" "}
            {unit}
          </Box>
        ) : null}
      </Typography>
    </Box>
    {icon ? (
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "14px",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255,255,255,0.18)",
          fontSize: 24,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    ) : null}
  </Box>
);

/** Small pill used for a row's status / type. */
export const MobileBadge = ({ label, tone = BRAND }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.35,
      borderRadius: 99,
      bgcolor: alpha(tone, 0.12),
      color: tone,
      fontSize: 11.5,
      fontWeight: 700,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {label}
  </Box>
);

/** Tinted square icon button for the card's action row. */
export const MobileAction = ({ title, icon, tone = BRAND, onClick, disabled }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          color: tone,
          bgcolor: alpha(tone, 0.1),
          "&:hover": { bgcolor: alpha(tone, 0.2) },
        }}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

/**
 * One row of the list.
 *
 * `fields` are `{ label, value, span }` stat tiles (span 6 = half width,
 * 12 = full). `highlight` is the row's headline amount and accepts
 * `tone: "auto"` to colour by sign.
 */
export const MobileListItem = ({
  avatar,
  avatarTone = BRAND,
  title,
  subtitle,
  titleStrikeThrough = false,
  badge,
  media,
  highlight,
  fields = [],
  note,
  noteLabel = "ملاحظات",
  actions,
}) => (
  <Box sx={{ ...CARD_SX, overflow: "hidden" }}>
    <Box sx={{ height: 3, background: BRAND_GRADIENT, opacity: 0.9 }} />
    <Stack spacing={1.5} sx={{ p: 1.75 }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        {avatar ? (
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(avatarTone, 0.12),
              color: avatarTone,
              fontSize: 20,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {avatar}
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              fontSize: 15,
              color: "#1f2a37",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textDecoration: titleStrikeThrough ? "line-through" : "none",
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              component="div"
              sx={{ fontSize: 11.5, color: "#667085" }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {badge}
      </Stack>

      {media ? (
        <Box sx={{ display: "grid", placeItems: "center" }}>{media}</Box>
      ) : null}

      {highlight ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: alpha(toneOf(highlight.rawValue, highlight.tone), 0.08),
            border: `1px solid ${alpha(
              toneOf(highlight.rawValue, highlight.tone),
              0.18
            )}`,
          }}
        >
          <Typography
            sx={{ fontSize: 12, color: "#475467", fontWeight: 600 }}
          >
            {highlight.label}
          </Typography>
          <Typography
            component="div"
            sx={{
              fontSize: 17,
              fontWeight: 800,
              color: toneOf(highlight.rawValue, highlight.tone),
              whiteSpace: "nowrap",
            }}
          >
            {highlight.value}
            {highlight.unit ? (
              <Box
                component="span"
                sx={{ fontSize: 11, fontWeight: 600, opacity: 0.8 }}
              >
                {" "}
                {highlight.unit}
              </Box>
            ) : null}
          </Typography>
        </Box>
      ) : null}

      {fields.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {fields.map((field, index) => (
            <Box
              key={index}
              sx={{
                gridColumn: field.span === 12 ? "1 / -1" : "span 1",
                bgcolor: "#f9fafb",
                border: "1px solid #f2f4f7",
                borderRadius: 2,
                px: 1.25,
                py: 0.9,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{ fontSize: 11, color: "#667085", mb: 0.25 }}
                noWrap
              >
                {field.label}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "#1f2a37",
                  wordBreak: "break-word",
                }}
              >
                {field.value === null ||
                field.value === undefined ||
                field.value === ""
                  ? "-"
                  : field.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {note ? (
        <Box
          sx={{
            bgcolor: alpha(BRAND, 0.05),
            border: `1px solid ${alpha(BRAND, 0.15)}`,
            borderRadius: 2,
            px: 1.25,
            py: 0.9,
          }}
        >
          <Typography sx={{ fontSize: 11, color: "#667085", mb: 0.25 }}>
            {noteLabel}
          </Typography>
          <Typography component="div" sx={{ fontSize: 13, color: "#344054" }}>
            {note}
          </Typography>
        </Box>
      ) : null}

      {actions ? (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ pt: 1.25, borderTop: "1px dashed #eaecf0" }}
        >
          {actions}
        </Stack>
      ) : null}
    </Stack>
  </Box>
);

/** Gradient section heading for a card stack inside a larger page. */
export const MobileSectionTitle = ({ title, icon }) => (
  <Box
    sx={{
      background: BRAND_GRADIENT,
      borderRadius: 3,
      px: 2,
      py: 1.5,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      boxShadow: "0 6px 16px rgba(12,134,182,0.25)",
    }}
  >
    {icon ? <Box sx={{ display: "flex", fontSize: 20 }}>{icon}</Box> : null}
    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{title}</Typography>
  </Box>
);

/** Empty placeholder shown when a filtered list returns nothing. */
export const MobileEmpty = ({ message = "لا توجد بيانات", icon }) => (
  <Box
    sx={{
      ...CARD_SX,
      py: 5,
      px: 2,
      display: "grid",
      placeItems: "center",
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "18px",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(BRAND, 0.1),
        color: BRAND,
        fontSize: 28,
      }}
    >
      {icon ?? <HiOutlineInbox />}
    </Box>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#667085" }}>
      {message}
    </Typography>
  </Box>
);

/** Placeholder cards shown while the first page is loading. */
export const MobileSkeletonList = ({ count = 4, fields = 4 }) => (
  <>
    {Array.from(new Array(count)).map((_, index) => (
      <Box key={index} sx={{ ...CARD_SX, overflow: "hidden" }}>
        <Box sx={{ height: 3, bgcolor: "#eaecf0" }} />
        <Stack spacing={1.5} sx={{ p: 1.75 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Skeleton variant="rounded" width={42} height={42} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={22} />
              <Skeleton variant="text" width="35%" height={14} />
            </Box>
          </Stack>
          <Skeleton variant="rounded" height={46} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {Array.from(new Array(fields)).map((__, i) => (
              <Skeleton key={i} variant="rounded" height={44} />
            ))}
          </Box>
        </Stack>
      </Box>
    ))}
  </>
);

/** Pagination bar styled to sit at the end of the card stack. */
export const MobilePagination = (props) => (
  <Box sx={{ ...CARD_SX, overflow: "hidden" }}>
    <TablePagination
      component="div"
      labelRowsPerPage="عدد الصفوف"
      sx={{
        "& .MuiTablePagination-toolbar": { px: 1, minHeight: 52 },
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
          { fontSize: 12, mb: 0, color: "#475467" },
        "& .MuiTablePagination-select": { fontSize: 12 },
      }}
      {...props}
    />
  </Box>
);

/** Stack wrapper that also renders the list's loading bar. */
export const MobileListShell = ({ loading, children }) => (
  <Stack spacing={1.25} sx={{ mt: 1 }}>
    {loading ? (
      <LinearProgress sx={{ borderRadius: 99, height: 3 }} />
    ) : null}
    {children}
  </Stack>
);
